import { useEffect, useRef, useState } from 'react';
import { TRACKED_REG } from '../lib/flightTrack';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { ExternalLink, Radio, History, Play, Pause } from 'lucide-react';
import { PageHeader } from '../components/PageHeader';
import type { LiveState } from '../lib/track';

const KLGB: [number, number] = [33.8178, -118.1517];
const TRACK_API = `${import.meta.env.BASE_URL}api/track`;
const HISTORY_API = `${import.meta.env.BASE_URL}api/track-history`;
const FLIGHTAWARE = `https://www.flightaware.com/live/flight/${TRACKED_REG}`;

interface HistPoint {
  t: number;
  lat: number;
  lon: number;
  alt: number | null;
  gs: number | null;
  trk: number | null;
}
interface HistInfo {
  date: string;
  durationMin: number;
  maxAlt: number;
}
type HistState = 'idle' | 'loading' | 'loaded' | 'empty' | 'error';

function planeIcon(deg: number, color = '#1f7a3d') {
  return L.divIcon({
    className: 'ajm-plane',
    html: `<svg width="28" height="28" viewBox="0 0 24 24" style="transform:rotate(${deg}deg);filter:drop-shadow(0 1px 1px rgba(0,0,0,.3))"><path d="M12 2 L19 21 L12 17 L5 21 Z" fill="${color}"/></svg>`,
    iconSize: [28, 28],
    iconAnchor: [14, 14],
  });
}

export function FlightTracker() {
  const mapEl = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const marker = useRef<L.Marker | null>(null);
  // Live breadcrumb trail accumulated while this page is open.
  const trail = useRef<[number, number][]>([]);
  const trailLine = useRef<L.Polyline | null>(null);
  // Historical replay (full path of the most recent flight from adsb.lol traces).
  const histLine = useRef<L.Polyline | null>(null);
  const histMarker = useRef<L.Marker | null>(null);
  const histPts = useRef<HistPoint[]>([]);
  const playTimer = useRef<number | null>(null);
  // Once viewing history, stop auto-recentering on the live position.
  const viewingHistory = useRef(false);
  const [live, setLive] = useState<LiveState | null>(null);
  const [liveErr, setLiveErr] = useState(false);
  const [histState, setHistState] = useState<HistState>('idle');
  const [histInfo, setHistInfo] = useState<HistInfo | null>(null);
  const [playing, setPlaying] = useState(false);

  // Init map once.
  useEffect(() => {
    if (!mapEl.current || mapRef.current) return;
    const map = L.map(mapEl.current, { zoomControl: true }).setView(KLGB, 11);
    // CARTO tiles now need an API key (they render "API KEY REQUIRED"); OSM is keyless.
    L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap contributors',
      maxZoom: 18,
    }).addTo(map);
    L.circleMarker(KLGB, { radius: 5, color: '#0B1B3A', fillColor: '#0B1B3A', fillOpacity: 1, weight: 1 })
      .addTo(map)
      .bindTooltip('KLGB (home base)');
    mapRef.current = map;
    return () => {
      if (playTimer.current) clearInterval(playTimer.current);
      map.remove();
      mapRef.current = null;
    };
  }, []);

  // Poll live position every 15s.
  const lastGoodAt = useRef(0);
  useEffect(() => {
    let on = true;
    async function poll() {
      try {
        const r = await fetch(`${TRACK_API}?reg=${TRACKED_REG}`);
        const j = await r.json();
        const ac = j.ac?.[0];
        if (!on) return;
        // The proxy reports an upstream failure (rate limit, outage) as `error` with
        // an empty `ac`. That is NOT "on the ground" — keep the last reading for a
        // minute, then say the feed is down. Before 10/2/2026 this rendered as parked.
        if (j.error && !ac) {
          if (Date.now() - lastGoodAt.current > 60_000) setLiveErr(true);
          return;
        }
        lastGoodAt.current = Date.now();
        setLiveErr(false);
        setLive(
          ac && typeof ac.lat === 'number'
            ? { flight: ac.flight?.trim(), lat: ac.lat, lon: ac.lon, alt: ac.alt_baro === 'ground' ? null : ac.alt_baro, gs: ac.gs ?? 0, trk: ac.track ?? 0 }
            : null
        );
      } catch {
        if (on) setLiveErr(true);
      }
    }
    poll();
    const iv = setInterval(poll, 15000);
    return () => {
      on = false;
      clearInterval(iv);
    };
  }, []);

  // Render the live marker + breadcrumb trail.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (live) {
      const pos: [number, number] = [live.lat, live.lon];
      if (!marker.current) marker.current = L.marker(pos, { icon: planeIcon(live.trk) }).addTo(map);
      else { marker.current.setLatLng(pos); marker.current.setIcon(planeIcon(live.trk)); }

      const last = trail.current[trail.current.length - 1];
      if (!last || last[0] !== pos[0] || last[1] !== pos[1]) {
        trail.current.push(pos);
        if (trail.current.length > 1000) trail.current.shift();
        if (!trailLine.current)
          trailLine.current = L.polyline(trail.current, { color: '#1f7a3d', weight: 3, opacity: 0.8 }).addTo(map);
        else trailLine.current.setLatLngs(trail.current);
      }

      if (!viewingHistory.current) map.setView(pos, Math.max(map.getZoom(), 10), { animate: true });
    } else if (marker.current) {
      marker.current.remove();
      marker.current = null;
    }
  }, [live]);

  function stopPlay() {
    if (playTimer.current) {
      clearInterval(playTimer.current);
      playTimer.current = null;
    }
    setPlaying(false);
  }

  function play() {
    const map = mapRef.current;
    const pts = histPts.current;
    if (!map || pts.length < 2) return;
    stopPlay();
    setPlaying(true);
    let i = 0;
    if (histMarker.current) histMarker.current.remove();
    const m = L.marker([pts[0].lat, pts[0].lon], { icon: planeIcon(pts[0].trk ?? 0, '#b45309') }).addTo(map);
    histMarker.current = m;
    // ~70ms/point → a full flight replays in ~25-30s regardless of real duration.
    playTimer.current = window.setInterval(() => {
      i++;
      if (i >= pts.length) {
        stopPlay();
        return;
      }
      const p = pts[i];
      m.setLatLng([p.lat, p.lon]);
      m.setIcon(planeIcon(p.trk ?? 0, '#b45309'));
    }, 70) as unknown as number;
  }

  async function loadHistory() {
    const map = mapRef.current;
    if (!map) return;
    setHistState('loading');
    stopPlay();
    try {
      const r = await fetch(`${HISTORY_API}?reg=${TRACKED_REG}`);
      const j = await r.json();
      const pts: HistPoint[] = (j.points ?? []).filter(
        (p: HistPoint) => typeof p.lat === 'number' && typeof p.lon === 'number'
      );
      if (pts.length < 2) {
        setHistState('empty');
        return;
      }
      histPts.current = pts;
      viewingHistory.current = true;
      const latlngs = pts.map((p) => [p.lat, p.lon]) as [number, number][];
      if (histLine.current) histLine.current.remove();
      if (histMarker.current) {
        histMarker.current.remove();
        histMarker.current = null;
      }
      histLine.current = L.polyline(latlngs, { color: '#b45309', weight: 3, opacity: 0.85 }).addTo(map);
      map.fitBounds(L.latLngBounds(latlngs), { padding: [30, 30] });
      const maxAlt = Math.max(...pts.map((p) => p.alt ?? 0));
      const durationMin = Math.round((pts[pts.length - 1].t - pts[0].t) / 60000);
      const date = new Date(pts[0].t).toLocaleString([], {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      });
      setHistInfo({ date, durationMin, maxAlt });
      setHistState('loaded');
    } catch {
      setHistState('error');
    }
  }

  const airborne = live !== null && !liveErr;

  return (
    <>
      <PageHeader
        title="Flight tracker"
        subtitle={`See ${TRACKED_REG} live, replay its most recent flight, or open full history.`}
        actions={
          <a href={FLIGHTAWARE} target="_blank" rel="noopener" className="btn-secondary inline-flex items-center gap-1.5 text-sm">
            <ExternalLink size={14} /> FlightAware
          </a>
        }
      />

      {/* Live in-app map */}
      <div className="card overflow-hidden">
        <div className="flex items-center gap-2 border-b border-slate-100 px-5 py-3">
          <Radio size={16} className={airborne ? 'text-emerald-600' : 'text-ink-subtle'} />
          <span className="font-display text-base font-semibold text-navy-900">Live</span>
          <span className={`ml-auto rounded-full px-2.5 py-0.5 text-xs font-semibold ${airborne ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-ink-muted'}`}>
            {liveErr ? 'Unavailable' : airborne ? 'Airborne' : 'On the ground'}
          </span>
        </div>
        {/* `isolate` keeps Leaflet's high z-index panes/controls from painting over
            the sticky header and mobile nav dropdown. */}
        <div ref={mapEl} className="isolate h-[50vh] min-h-[320px] w-full bg-slate-100" />
        <div className="flex flex-wrap items-center gap-x-8 gap-y-3 px-5 py-4">
          <Readout label="Altitude" value={live?.alt != null ? `${live.alt.toLocaleString()} ft` : '—'} />
          <Readout label="Ground speed" value={live ? `${Math.round(live.gs)} kt` : '—'} />
          <Readout label="Heading" value={live ? `${Math.round(live.trk)}°` : '—'} />
          <p className="w-full text-xs text-ink-muted">
            {liveErr
              ? 'Live ADS-B feed unavailable right now (the public network is rate-limiting or down). Retrying every 15 seconds — the flight path below and FlightAware still work.'
              : airborne
              ? 'Updating every 15 seconds from the public ADS-B network — the green line traces the route as it flies.'
              : `${TRACKED_REG} isn’t airborne right now. It appears here automatically the moment it takes off — no setup needed.`}
          </p>
        </div>
      </div>

      {/* In-app replay of the most recent flight */}
      <div className="card mt-4 overflow-hidden">
        <div className="flex items-center gap-2 border-b border-slate-100 px-5 py-3">
          <History size={16} className="text-amber-600" />
          <span className="font-display text-base font-semibold text-navy-900">Recent flight path</span>
          {histState === 'loaded' && (
            <button
              onClick={playing ? stopPlay : play}
              className="ml-auto btn-secondary inline-flex items-center gap-1.5 py-1.5 text-sm"
            >
              {playing ? <><Pause size={14} /> Stop</> : <><Play size={14} /> Replay</>}
            </button>
          )}
        </div>
        <div className="px-5 py-4">
          {histState === 'idle' && (
            <button onClick={loadHistory} className="btn-primary inline-flex items-center gap-1.5 text-sm">
              <History size={15} /> Load most recent flight
            </button>
          )}
          {histState === 'loading' && <p className="text-sm text-ink-muted">Loading the track…</p>}
          {histState === 'empty' && (
            <p className="text-sm text-ink-muted">
              No recent track — {TRACKED_REG} hasn’t been seen on ADS-B in the last day. The full path shows up here
              automatically after its next flight.
            </p>
          )}
          {histState === 'error' && (
            <p className="text-sm text-ink-muted">
              Couldn’t load the track.{' '}
              <button onClick={loadHistory} className="font-medium text-navy-700 underline">
                Try again
              </button>
              .
            </p>
          )}
          {histState === 'loaded' && histInfo && (
            <div className="flex flex-wrap items-center gap-x-8 gap-y-3">
              <Readout label="Flight" value={histInfo.date} />
              <Readout label="Duration" value={`${histInfo.durationMin} min`} />
              <Readout label="Max altitude" value={`${histInfo.maxAlt.toLocaleString()} ft`} />
              <p className="w-full text-xs text-ink-muted">
                The amber line is the full path. Tap <strong>Replay</strong> to watch it fly the route.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Older flights via FlightAware (beyond the most recent day) */}
      <a
        href={FLIGHTAWARE}
        target="_blank"
        rel="noopener"
        className="card mt-4 flex items-center gap-4 p-5 transition hover:shadow-lift"
      >
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-sky-50 text-navy-700">
          <ExternalLink size={20} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="font-display text-base font-semibold text-navy-900">Older flights</div>
          <div className="text-sm text-ink-muted">
            For flights beyond the most recent day, FlightAware keeps every flight’s full replay — path,
            altitude, and speed — indefinitely.
          </div>
        </div>
        <ExternalLink size={18} className="shrink-0 text-ink-subtle" />
      </a>
    </>
  );
}

function Readout({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-ink-subtle">{label}</div>
      <div className="font-display text-lg font-semibold text-navy-900">{value}</div>
    </div>
  );
}
